import { StrictMode, Suspense } from "react";

import "@vericus/cadmus-common/dist/style.css";
import { CssBaseline, CUIProvider } from "@vericus/cadmus-ui";
import "@vericus/cadmus-ui/dist/index.css";

import { ApolloProvider } from "@apollo/client";
import { JitsuProvider } from "@jitsu/react";
import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "core-js/features/string/match-all";
import "core-js/features/string/replace-all";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import "regenerator-runtime/runtime";

import apolloClient from "@/client/apollo";
import jitsuClient from "@/client/jitsu";
import { connection, PhoenixContext } from "@/client/phoenix";
import reduxStore from "@/data/store";
import routes from "@/router/routes";
import { CrashError } from "@/ui/errors";
import { Loading } from "@/ui/shared/Loading";

import { theme } from "./index.css";
import "./instrument";
import { MantineProvider } from "./styles/mantine";
import "./styles/third-party.css";
import { registerTemplatesModals } from "./ui/templates";

import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "mathlive/dist/mathlive-static.css";

// Register template modals with modal state management
registerTemplatesModals();

// React root
const container = document.getElementById("root");
const root = createRoot(container!);

// Update method whenever react-router-dom updates
const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);

const router = sentryCreateBrowserRouter(routes);

const queryClient = new QueryClient();

root.render(
  <StrictMode>
    <MantineProvider>
      <CUIProvider theme={theme}>
        <Sentry.ErrorBoundary fallback={<CrashError />}>
          <PhoenixContext.Provider value={connection}>
            <JitsuProvider client={jitsuClient}>
              <ApolloProvider client={apolloClient}>
                <Provider store={reduxStore}>
                  <QueryClientProvider client={queryClient}>
                    <Suspense fallback={<Loading />}>
                      <CssBaseline />
                      <RouterProvider router={router} />
                    </Suspense>
                  </QueryClientProvider>
                </Provider>
              </ApolloProvider>
            </JitsuProvider>
          </PhoenixContext.Provider>
        </Sentry.ErrorBoundary>
      </CUIProvider>
    </MantineProvider>
  </StrictMode>
);
